import { useNavigate } from 'react-router-dom';
import { Scissors, ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import SEO, { breadcrumbSchema } from '../components/SEO';

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '28px' }}>
    <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', marginBottom: '10px', fontFamily: "'Syne', sans-serif" }}>{title}</h2>
    <div style={{ color: '#4b5563', fontSize: '0.85rem', lineHeight: 1.8 }}>{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa' }}>
      <SEO
        title="Privacy Policy"
        description="How BookYourSalons collects, uses and protects your personal data. Read our privacy commitments on OTP login, location access and booking information."
        path="/privacy"
        schema={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Privacy Policy', path: '/privacy' },
        ])}
      />
      <div style={{
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '12px 20px',
        display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#374151', fontWeight: 600, fontSize: '0.85rem' }}>
          <ArrowLeft size={18} /> Back
        </button>
        <span style={{ fontWeight: 800, fontFamily: "'Syne', sans-serif", fontSize: '0.9rem', color: '#dc2626' }}>
          Privacy Policy
        </span>
      </div>

      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '36px 20px' }}>
        <div style={{ background: 'linear-gradient(135deg, #dc2626, #e11d48)', borderRadius: '16px', padding: '24px', marginBottom: '32px', color: 'white' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, fontFamily: "'Syne', sans-serif", marginBottom: '8px' }}>Privacy Policy</h1>
          <p style={{ opacity: 0.85, fontSize: '0.82rem' }}>Last updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <p style={{ opacity: 0.85, fontSize: '0.82rem', marginTop: '4px' }}>BookYourSalons Private Limited</p>
        </div>

        <div style={{ background: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>

          <Section title="1. Information We Collect">
            <p>We collect the following information when you use BookYourSalons:</p>
            <ul style={{ paddingLeft: '18px', marginTop: '8px' }}>
              <li><strong>Phone Number:</strong> For OTP-based authentication</li>
              <li><strong>Name:</strong> For booking identification</li>
              <li><strong>Location:</strong> To show nearby salons (only with your permission)</li>
              <li><strong>Booking History:</strong> To manage your appointments</li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <ul style={{ paddingLeft: '18px' }}>
              <li>To authenticate your account via OTP</li>
              <li>To process and confirm salon bookings</li>
              <li>To show salons near your location</li>
              <li>To send booking status notifications</li>
              <li>To improve our platform and services</li>
            </ul>
          </Section>

          <Section title="3. Data Sharing">
            <p>We do <strong>not</strong> sell, trade, or rent your personal information to third parties. We only share data with:</p>
            <ul style={{ paddingLeft: '18px', marginTop: '8px' }}>
              <li><strong>Salon Partners:</strong> Your name and phone for confirmed bookings</li>
              <li><strong>Firebase (Google):</strong> Authentication and database services</li>
            </ul>
          </Section>

          <Section title="4. Data Security">
            <p>We implement industry-standard security measures including:</p>
            <ul style={{ paddingLeft: '18px', marginTop: '8px' }}>
              <li>HTTPS encryption for all data transmission</li>
              <li>Firebase Authentication with OTP verification</li>
              <li>Firestore security rules — your data is access-controlled</li>
              <li>Rate limiting to prevent unauthorized access</li>
            </ul>
          </Section>

          <Section title="5. Location Data">
            <p>Location access is requested only to show nearby salons. We do not store or track your location history. Location data is processed in real-time and is never saved to our servers.</p>
          </Section>

          <Section title="6. Your Rights">
            <p>You have the right to:</p>
            <ul style={{ paddingLeft: '18px', marginTop: '8px' }}>
              <li>Access your personal data</li>
              <li>Delete your account and data</li>
              <li>Opt out of notifications</li>
            </ul>
            <p style={{ marginTop: '8px' }}>To exercise these rights, email us at: <a href="mailto:bookyoursalons@gmail.com" style={{ color: '#dc2626' }}>bookyoursalons@gmail.com</a></p>
          </Section>

          <Section title="7. Contact Us">
            <p>For any privacy-related questions or concerns:</p>
            <p style={{ marginTop: '8px' }}>📧 <a href="mailto:bookyoursalons@gmail.com" style={{ color: '#dc2626' }}>bookyoursalons@gmail.com</a></p>
            <p>🏢 BookYourSalons Private Limited, India</p>
          </Section>

          <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '16px', color: '#9ca3af', fontSize: '0.75rem', textAlign: 'center' }}>
            © {year} BookYourSalons Private Limited. All rights reserved.
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
